/**
 * BabyProofMe Editorial Module JS
 * - TOC pill: expand/collapse, active section tracking, auto-show/hide
 * - Checklist: persist checked state to localStorage
 * - Smooth scroll for anchor links
 */
(function () {
  'use strict';

  // =========================================================================
  // TOC Pill — Floating section navigator
  // =========================================================================
  const tocPill = document.querySelector('[data-ed-toc]');
  if (tocPill) {
    const toggle = tocPill.querySelector('[data-ed-toc-toggle]');
    const panel = tocPill.querySelector('[data-ed-toc-panel]');
    const closeBtn = tocPill.querySelector('[data-ed-toc-close]');
    const links = tocPill.querySelectorAll('[data-ed-toc-link]');
    const sections = [];

    // Collect sections referenced by TOC links
    links.forEach(link => {
      const id = link.getAttribute('href')?.replace('#', '');
      const el = id ? document.getElementById(id) : null;
      if (el) sections.push({ id, el, link });
    });

    // Toggle expand/collapse
    function expandToc() {
      tocPill.classList.add('ed-toc--expanded');
      toggle.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
    }

    function collapseToc() {
      tocPill.classList.remove('ed-toc--expanded');
      toggle.setAttribute('aria-expanded', 'false');
      panel.setAttribute('hidden', '');
    }

    if (toggle) {
      toggle.addEventListener('click', () => {
        const isOpen = tocPill.classList.contains('ed-toc--expanded');
        isOpen ? collapseToc() : expandToc();
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', collapseToc);
    }

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (tocPill.classList.contains('ed-toc--expanded') && !tocPill.contains(e.target)) {
        collapseToc();
      }
    });

    // Smooth scroll on link click
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const id = link.getAttribute('href')?.replace('#', '');
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          collapseToc();
          // Update URL hash without scrolling
          history.replaceState(null, '', '#' + id);
        }
      });
    });

    // Active section tracking via IntersectionObserver
    if (sections.length > 0 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            links.forEach(l => {
              l.classList.toggle('ed-toc__link--active',
                l.getAttribute('href') === '#' + id);
            });
          }
        });
      }, {
        rootMargin: '-10% 0px -80% 0px',
        threshold: 0,
      });

      sections.forEach(s => observer.observe(s.el));
    }

    // Show/hide TOC based on scroll position
    let lastScroll = 0;
    const firstH2 = document.querySelector('.ed-article h2, .ed-article [data-ed-section]');
    const footer = document.querySelector('footer, .footer-group');

    function updateTocVisibility() {
      const scroll = window.scrollY;
      const firstH2Top = firstH2 ? firstH2.getBoundingClientRect().top + scroll : 300;
      const footerTop = footer ? footer.getBoundingClientRect().top : Infinity;

      if (scroll > firstH2Top && footerTop > window.innerHeight) {
        tocPill.removeAttribute('hidden');
      } else {
        tocPill.setAttribute('hidden', '');
        collapseToc();
      }

      lastScroll = scroll;
    }

    // Throttled scroll handler
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateTocVisibility();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Initial check
    updateTocVisibility();
  }

  // =========================================================================
  // Checklist — Persist checked state
  // =========================================================================
  const checklists = document.querySelectorAll('.ed-checklist');
  checklists.forEach(checklist => {
    const key = 'ed-checklist-' + (checklist.id || checklist.dataset.edChecklistId || 'default');
    const inputs = checklist.querySelectorAll('input[type="checkbox"]');

    // Restore state
    try {
      const saved = JSON.parse(localStorage.getItem(key) || '{}');
      inputs.forEach(input => {
        if (saved[input.id]) input.checked = true;
      });
    } catch {}

    // Save on change
    inputs.forEach(input => {
      input.addEventListener('change', () => {
        const state = {};
        inputs.forEach(i => { if (i.checked) state[i.id] = true; });
        try { localStorage.setItem(key, JSON.stringify(state)); } catch {}

        // Update progress bar if present
        const progress = checklist.querySelector('.ed-checklist__progress-fill');
        if (progress) {
          const total = inputs.length;
          const checked = checklist.querySelectorAll('input:checked').length;
          progress.style.width = (checked / total * 100) + '%';
        }
      });
    });
  });

  // =========================================================================
  // Smooth scroll for all editorial anchor links
  // =========================================================================
  document.querySelectorAll('.ed-article a[href^="#"]').forEach(link => {
    link.addEventListener('click', (e) => {
      const id = link.getAttribute('href').replace('#', '');
      const target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // =========================================================================
  // Checklist — Progress bar, counter, and completion badge
  // =========================================================================
  document.querySelectorAll('.ed-checklist').forEach(checklist => {
    const inputs = checklist.querySelectorAll('input[type="checkbox"]');
    const progressFill = checklist.querySelector('.ed-checklist__progress-fill');
    const progressText = checklist.querySelector('.ed-checklist__progress-text');
    const progressBar = checklist.querySelector('.ed-checklist__progress-bar');
    const completeBadge = checklist.querySelector('.ed-checklist__complete-badge');
    const total = inputs.length;

    function updateProgress() {
      const checked = checklist.querySelectorAll('input:checked').length;

      if (progressFill) {
        const pct = total > 0 ? (checked / total * 100) : 0;
        progressFill.style.width = pct + '%';
      }

      if (progressBar) {
        progressBar.setAttribute('aria-valuenow', checked);
      }

      if (progressText) {
        progressText.textContent = checked + ' of ' + total + ' complete';
      }

      if (completeBadge) {
        if (checked === total && total > 0) {
          completeBadge.removeAttribute('hidden');
          checklist.classList.add('ed-checklist--complete');
        } else {
          completeBadge.setAttribute('hidden', '');
          checklist.classList.remove('ed-checklist--complete');
        }
      }
    }

    // Run on load to sync with restored localStorage state
    updateProgress();

    // Update on each change
    inputs.forEach(input => {
      input.addEventListener('change', updateProgress);
    });
  });

  // =========================================================================
  // Danger Spotlight — Tooltip toggle on tap (mobile) and hover (desktop)
  // =========================================================================
  document.querySelectorAll('.ed-spotlight').forEach(spotlight => {
    const dots = spotlight.querySelectorAll('[data-ed-spotlight-dot]');
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    function hideAllTooltips() {
      spotlight.querySelectorAll('[data-ed-spotlight-tooltip]').forEach(tip => {
        tip.setAttribute('hidden', '');
        tip.classList.remove('ed-spotlight__tooltip--visible');
        tip.setAttribute('aria-hidden', 'true');
      });
      dots.forEach(d => d.setAttribute('aria-expanded', 'false'));
    }

    function showTooltip(number) {
      const tip = spotlight.querySelector('[data-ed-spotlight-tooltip="' + number + '"]');
      const dot = spotlight.querySelector('[data-ed-spotlight-dot="' + number + '"]');
      if (tip) {
        tip.removeAttribute('hidden');
        tip.classList.add('ed-spotlight__tooltip--visible');
        tip.setAttribute('aria-hidden', 'false');
      }
      if (dot) {
        dot.setAttribute('aria-expanded', 'true');
      }
    }

    dots.forEach(dot => {
      const number = dot.getAttribute('data-ed-spotlight-dot');

      // Tap / click toggle (works on both mobile and desktop)
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = dot.getAttribute('aria-expanded') === 'true';
        hideAllTooltips();
        if (!isOpen) {
          showTooltip(number);
        }
      });

      // Hover for desktop (non-touch)
      if (!isTouchDevice) {
        dot.addEventListener('mouseenter', () => {
          hideAllTooltips();
          showTooltip(number);
        });

        dot.addEventListener('mouseleave', () => {
          hideAllTooltips();
        });
      }

      // Keyboard: Enter/Space
      dot.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          dot.click();
        }
      });
    });

    // Click outside to close tooltips
    document.addEventListener('click', (e) => {
      if (!spotlight.contains(e.target)) {
        hideAllTooltips();
      }
    });
  });

  // =========================================================================
  // Stat Shimmer — Trigger animation when stat scrolls into view
  // =========================================================================
  if ('IntersectionObserver' in window) {
    const statNumbers = document.querySelectorAll('.ed-stat__number--shimmer');
    if (statNumbers.length > 0) {
      const statObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('ed-stat__number--active');
            statObserver.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.1,
      });

      statNumbers.forEach(el => statObserver.observe(el));
    }
  }

})();
